import React from 'react';
import ImportReviewSummary from './ImportReviewSummary';
import ImportReviewTable from './ImportReviewTable';
import OrderIssuesDrawer from './OrderIssuesDrawer';
import ImportBulkActions from './ImportBulkActions';
import {
	filterReviewOrders,
	getImportRowIdentity,
	getOrderIdentifier,
	getReviewSummary,
	getZoneCoverageChangeCount,
	REVIEW_FILTER,
} from './importReview.model';
import styles from './ImportReview.module.css';

const ImportReviewWorkspace = ({
	orders,
	fields,
	templateValidatorFields,
	locationFields: effectiveLocationFields,
	onApplyCandidate,
	onSelectZoneCreation,
	onUndoZoneCreation,
	onApplyService,
	onStageLegacyService,
	onUndoServiceSelection,
	onRequestServiceCreation,
	onSelectServiceCreation,
	onUndoServiceCreation,
	manualZoneSelector,
	manualServiceSelector,
	frequencySelector,
	assignmentSelector,
	assignmentReviewSelections = [],
	onStageAssignmentSelection,
	onApplyFrequency,
	onRequestFrequencyCreation,
	onSelectFrequencyCreation,
	onUndoFrequencyCreation,
	onDiscardFrequencyCreationRequest,
	frequencyCreationRequests = [],
	frequencyCreationSelections = [],
	fieldReviewSelections = [],
	onApplyFieldReview,
	onUndoFieldReview,
	onDeleteRows,
	serviceCreationSelections = [],
	zoneCreationSelections = [],
	zoneCreationSummary = { zoneCount: 0, affectedOrderCount: 0 },
	selectedBulkRowIds = [],
	onSelectedBulkRowIdsChange = () => {},
	onApplyBulkAssignment,
	onApplyBulkService,
	onApplyBulkZone,
	onApplyBulkFrequency,
	children,
	themeStyle,
	themeProfile,
	isBusy = false,
	isInteractionBlocked = false,
	onBulkConfirmationPendingChange = () => {},
	importReviewError,
}) => {
	const [activeFilter, setActiveFilter] = React.useState(REVIEW_FILTER.ALL);
	const [search, setSearch] = React.useState('');
	const [selectedOrder, setSelectedOrder] = React.useState();
	const [view, setView] = React.useState('review');

	const {
		odtIdField,
		cityIdField,
		townshipIdField,
		contractZoneIdField,
		orderZoneIdField,
		serviceIdField,
		contractFrequencyIdField,
	} = templateValidatorFields;
	const locationFields = React.useMemo(
		() =>
			effectiveLocationFields || {
				cityIdField,
				townshipIdField,
				zoneFields: [contractZoneIdField, orderZoneIdField].filter(Boolean),
			},
		[
			effectiveLocationFields,
			cityIdField,
			townshipIdField,
			contractZoneIdField,
			orderZoneIdField,
		],
	);
	const summary = React.useMemo(
		() =>
			getReviewSummary(orders, fields, locationFields, {
				contractFrequencyCreationSelections: frequencyCreationSelections,
			}),
		[orders, fields, locationFields, frequencyCreationSelections],
	);
	const zoneCoverageChangeCount = React.useMemo(
		() => getZoneCoverageChangeCount(orders),
		[orders],
	);
	const filteredOrders = React.useMemo(
		() =>
			filterReviewOrders({
				orders,
				fields,
				filter: activeFilter,
				search,
				odtIdField,
				locationFields,
				contractFrequencyCreationSelections: frequencyCreationSelections,
			}),
		[
			orders,
			fields,
			activeFilter,
			search,
			odtIdField,
			locationFields,
			frequencyCreationSelections,
		],
	);
	const selectedOrderInState = React.useMemo(
		() =>
			selectedOrder
				? orders.find(
						candidate =>
							getImportRowIdentity(candidate) ===
								getImportRowIdentity(selectedOrder) ||
							(getOrderIdentifier(candidate, odtIdField) ===
								getOrderIdentifier(selectedOrder, odtIdField) &&
								candidate?.importDiagnostics?.rowNumber ===
									selectedOrder?.importDiagnostics?.rowNumber),
					)
				: undefined,
		[orders, selectedOrder, odtIdField],
	);
	const nextOrderLabel = [
		REVIEW_FILTER.REVIEW_REQUIRED,
		REVIEW_FILTER.BLOCKED,
	].includes(activeFilter)
		? 'Siguiente problema'
		: 'Siguiente';
	const interactionBlocked = isBusy || isInteractionBlocked;
	const rowLabelsById = React.useMemo(
		() =>
			new Map(
				orders.map(order => [
					String(getImportRowIdentity(order)),
					order?.importDiagnostics?.rowNumber || getImportRowIdentity(order),
				]),
			),
		[orders],
	);

	React.useEffect(() => {
		if (selectedOrder && !selectedOrderInState) setSelectedOrder();
	}, [selectedOrder, selectedOrderInState]);

	const onChangeView = nextView => {
		if (nextView === 'data') {
			// The full editor always shows every order. Reset hidden review controls
			// so returning to review cannot revive an invisible filter/query.
			setActiveFilter(REVIEW_FILTER.ALL);
			setSearch('');
		}
		if (nextView === 'review') onSelectedBulkRowIdsChange([]);
		setView(nextView);
	};

	return (
		<div
			className={styles.workspace}
			style={themeStyle}
			data-theme={themeProfile}
			aria-busy={isBusy}
		>
			{importReviewError && (
				<section className={styles.contractZoneConflictNotice} role='alert'>
					<strong>{importReviewError.message}</strong>
					<p>
						Cada contrato requiere una única zona contractual. Si estas órdenes
						necesitan zonas distintas, modélalas en la zona de orden.
					</p>
					<ul>
						{importReviewError.contractZoneConflicts.map(conflict => (
							<li key={conflict.contractNumber}>
								Contrato {conflict.contractNumber} · Filas{' '}
								{conflict.rowIds
									.map(rowId => rowLabelsById.get(String(rowId)) || rowId)
									.join(', ')}
							</li>
						))}
					</ul>
				</section>
			)}
			{view === 'review' && (
				<ImportReviewSummary
					summary={summary}
					activeFilter={activeFilter}
					onChange={setActiveFilter}
					zoneCoverageChangeCount={zoneCoverageChangeCount}
					zoneCreationCount={zoneCreationSummary.zoneCount}
					zoneCreationAffectedOrderCount={
						zoneCreationSummary.affectedOrderCount
					}
					disabled={interactionBlocked}
				/>
			)}
			<div className={styles.toolbar}>
				{view === 'review' ? (
					<label className={styles.searchField}>
						<span className={styles.srOnly}>Buscar órdenes o ubicaciones</span>
						<span aria-hidden='true'>⌕</span>
						<input
							type='search'
							value={search}
							placeholder='Buscar orden, ubicación o problema'
							onChange={event => setSearch(event.target.value)}
							disabled={interactionBlocked}
						/>
					</label>
				) : (
					<span className={styles.dataViewHint}>
						Edita los campos y valida los cambios antes de importar.
					</span>
				)}
				<div
					className={styles.viewSwitch}
					role='group'
					aria-label='Vista de órdenes'
				>
					<button
						type='button'
						aria-pressed={view === 'review'}
						className={view === 'review' ? styles.viewSwitchActive : ''}
						onClick={() => onChangeView('review')}
						disabled={interactionBlocked}
					>
						Revisión
					</button>
					<button
						type='button'
						aria-pressed={view === 'data'}
						className={view === 'data' ? styles.viewSwitchActive : ''}
						onClick={() => onChangeView('data')}
						disabled={interactionBlocked}
					>
						Todos los campos
					</button>
				</div>
			</div>
			{view === 'data' && (
				<ImportBulkActions
					orders={orders}
					selectedRowIds={selectedBulkRowIds}
					onSelectedRowIdsChange={onSelectedBulkRowIdsChange}
					assignedTechIdField={templateValidatorFields.assignedTechIdField}
					serviceIdField={serviceIdField}
					contractFrequencyIdField={contractFrequencyIdField}
					locationFields={locationFields}
					zoneFields={locationFields.zoneFields}
					onApplyAssignment={onApplyBulkAssignment}
					onApplyService={onApplyBulkService}
					onApplyZone={onApplyBulkZone}
					onApplyFrequency={onApplyBulkFrequency}
					onPendingChange={onBulkConfirmationPendingChange}
					isBusy={isBusy}
				/>
			)}

			{view === 'review' ? (
				<>
					<div className={styles.srOnly} role='status' aria-live='polite'>
						Mostrando {filteredOrders.length} de {orders.length} órdenes.
					</div>
					<ImportReviewTable
						orders={filteredOrders}
						fields={fields}
						odtIdField={odtIdField}
						locationFields={locationFields}
						assignedTechIdField={templateValidatorFields.assignedTechIdField}
						frequencyCreationSelections={frequencyCreationSelections}
						onOpenOrder={setSelectedOrder}
						isSubmitting={interactionBlocked}
					/>
				</>
			) : (
				children
			)}

			<OrderIssuesDrawer
				order={selectedOrderInState}
				orders={filteredOrders}
				reviewOrders={orders}
				fields={fields}
				odtIdField={odtIdField}
				locationFields={locationFields}
				assignedTechIdField={templateValidatorFields.assignedTechIdField}
				serviceIdField={serviceIdField}
				contractFrequencyIdField={contractFrequencyIdField}
				onClose={() => setSelectedOrder()}
				onOpenOrder={setSelectedOrder}
				onApplyCandidate={onApplyCandidate}
				onSelectZoneCreation={onSelectZoneCreation}
				onUndoZoneCreation={onUndoZoneCreation}
				onApplyService={onApplyService}
				onStageLegacyService={onStageLegacyService}
				onUndoServiceSelection={onUndoServiceSelection}
				onRequestServiceCreation={onRequestServiceCreation}
				onSelectServiceCreation={onSelectServiceCreation}
				onUndoServiceCreation={onUndoServiceCreation}
				nextOrderLabel={nextOrderLabel}
				serviceCreationSelections={serviceCreationSelections}
				zoneCreationSelections={zoneCreationSelections}
				manualZoneSelector={manualZoneSelector}
				manualServiceSelector={manualServiceSelector}
				frequencySelector={frequencySelector}
				assignmentSelector={assignmentSelector}
				assignmentReviewSelection={assignmentReviewSelections.find(
					selection =>
						String(selection.rowId) ===
						String(getImportRowIdentity(selectedOrderInState)),
				)}
				onStageAssignmentSelection={onStageAssignmentSelection}
				onApplyFrequency={onApplyFrequency}
				onRequestFrequencyCreation={onRequestFrequencyCreation}
				onSelectFrequencyCreation={onSelectFrequencyCreation}
				onUndoFrequencyCreation={onUndoFrequencyCreation}
				onDiscardFrequencyCreationRequest={onDiscardFrequencyCreationRequest}
				frequencyCreationRequests={frequencyCreationRequests}
				frequencyCreationSelections={frequencyCreationSelections}
				fieldReviewSelections={fieldReviewSelections}
				onApplyFieldReview={onApplyFieldReview}
				onUndoFieldReview={onUndoFieldReview}
				onDeleteOrder={onDeleteRows}
				themeStyle={themeStyle}
				themeProfile={themeProfile}
				isSubmitting={interactionBlocked}
			/>
		</div>
	);
};

export default ImportReviewWorkspace;
